import type {
    AbsorbedFees,
    AbsorbedFeesView,
    AgreementFinancials,
    PlanCost,
    RelatedRow,
    StatementRow,
    StatementSection,
    StatementTotals,
} from '../types/financials.types';
import { absorbedFeeSubtitle } from './absorbedFeeSubtitle';

export interface TotalsEntry {
    key: keyof StatementTotals;
    label: string;
    value: string;
}

export interface StatementRowView {
    id: string;
    row: StatementRow;
}

export interface AmountCells {
    net: string | null;
    vat: string | null;
    gross: string;
}

export interface StatementView {
    currency: string;
    asOf: string;
    planCost: PlanCost;
    balanceTotals: TotalsEntry[];
    scheduledTotals: TotalsEntry[];
    rows: StatementRowView[];
    related: RelatedRow[];
    degraded: boolean;
    detail: boolean;
    truncated: boolean;
    scheduledPaymentsUnavailable: boolean;
    absorbedFees: AbsorbedFeesView | null;
}

const BALANCE_TOTALS_ORDER: ReadonlyArray<{ key: keyof StatementTotals; label: string }> = [
    { key: 'collectedIn', label: 'Collected in' },
    { key: 'paidOut', label: 'Paid out' },
    { key: 'balance', label: 'Balance' },
];

const SCHEDULED_TOTALS_ORDER: ReadonlyArray<{ key: keyof StatementTotals; label: string }> = [
    { key: 'scheduledIn', label: 'Scheduled in' },
    { key: 'scheduledOut', label: 'Scheduled out' },
];

const DEBIT_SECTIONS: ReadonlySet<StatementSection> = new Set(['paid_out', 'scheduled_out']);

const ZERO_MAGNITUDE = /^0+(\.0+)?$/;

export const signedAmount = (section: StatementSection, amount: string): string => {
    const magnitude = amount.startsWith('-') ? amount.slice(1) : amount;
    if (ZERO_MAGNITUDE.test(magnitude)) return magnitude;
    return DEBIT_SECTIONS.has(section) ? `-${magnitude}` : magnitude;
};

export const signedAmounts = (row: StatementRow): AmountCells => ({
    net: row.net === null ? null : signedAmount(row.section, row.net),
    vat: row.vat === null ? null : signedAmount(row.section, row.vat),
    gross: signedAmount(row.section, row.amount),
});

export const rowId = (row: StatementRow, index: number): string =>
    [row.date, row.section, row.kind, row.transactionCode ?? row.serviceSequence ?? '', index].join('|');

const totalsEntries = (
    order: ReadonlyArray<{ key: keyof StatementTotals; label: string }>,
    totals: StatementTotals,
): TotalsEntry[] => order.map(({ key, label }) => ({ key, label, value: totals[key] }));

export const balanceTotalsView = (totals: StatementTotals): TotalsEntry[] =>
    totalsEntries(BALANCE_TOTALS_ORDER, totals);

export const scheduledTotalsView = (totals: StatementTotals): TotalsEntry[] =>
    totalsEntries(SCHEDULED_TOTALS_ORDER, totals);

export type StatementStatus = 'reconciled' | 'pending' | 'scheduled' | 'overdue' | 'held';

export const isFutureScheduled = (row: StatementRow, asOf: string): boolean =>
    row.state !== 'settled' && row.date > asOf;

export const statusOf = (row: StatementRow, asOf: string): StatementStatus => {
    if (row.state === 'settled') return 'reconciled';
    if (row.state === 'overdue') return 'overdue';
    if (row.state === 'held') return 'held';
    return row.date > asOf ? 'scheduled' : 'pending';
};

export const visibleRows = (rows: StatementRowView[], asOf: string, showUnreconciled: boolean): StatementRowView[] =>
    showUnreconciled ? rows : rows.filter((entry) => !isFutureScheduled(entry.row, asOf));

export const futureScheduledCount = (rows: StatementRowView[], asOf: string): number =>
    rows.filter((entry) => isFutureScheduled(entry.row, asOf)).length;

export const absorbedFeesView = (fees: AbsorbedFees | null): AbsorbedFeesView | null => {
    if (fees === null) return null;
    if (fees.status === 'unavailable') return { rows: [], reconstructed: fees.reconstructed, unavailable: true };
    if (fees.lines.length === 0) return null;
    return {
        rows: fees.lines.map((line, index) => ({
            id: `${line.productFeeId}|${index}`,
            label: line.label,
            subtitle: absorbedFeeSubtitle(line),
            net: line.net,
            vat: line.vat,
            gross: line.gross,
        })),
        reconstructed: fees.reconstructed,
        unavailable: false,
    };
};

export const statementView = (financials: AgreementFinancials): StatementView => {
    const identified = financials.rows.map((row, index) => ({ id: rowId(row, index), row }));

    return {
        currency: financials.currency,
        asOf: financials.asOf,
        planCost: financials.planCost,
        balanceTotals: balanceTotalsView(financials.totals),
        scheduledTotals: scheduledTotalsView(financials.totals),
        rows: identified,
        related: financials.related,
        degraded: financials.visibility.degraded,
        detail: financials.visibility.detail,
        truncated: financials.truncated,
        scheduledPaymentsUnavailable: financials.scheduledPayments.status === 'unavailable',
        absorbedFees: absorbedFeesView(financials.absorbedFees),
    };
};
